import { Link, useParams } from "react-router-dom";
import { ArrowRight, Building2, CheckCircle2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Breadcrumbs, COMPANY_EMAIL, COMPANY_NAME, DISTRICTS, PHONE_PRIMARY, PageHero,
  SERVICES, SERVICE_OG_IMAGE, SITE_ORIGIN, SectionTag, SiteLayout, useLeadModal, useSEO,
} from "./shared";
import { SERVICE_CONTENT } from "./serviceContent";
import { NotFoundContent } from "./NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { openQuote } = useLeadModal();
  const service = SERVICES.find((s) => s.slug === slug);
  const content = service ? SERVICE_CONTENT[slug] : null;
  const valid = Boolean(service && content);

  const title = valid
    ? `${service.title} in Tripura | SecureFlame Fire Systems`
    : "Service Not Found | SecureFlame Fire Systems";
  const description = valid
    ? content.metaDescription
    : "The service you're looking for could not be found on the SecureFlame Fire Systems website.";
  const jsonLd = valid
    ? [
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: service.title,
          description: content.metaDescription,
          url: `${SITE_ORIGIN}/services/${slug}`,
          provider: {
            "@type": "Organization",
            name: COMPANY_NAME,
            telephone: PHONE_PRIMARY,
            email: COMPANY_EMAIL,
            url: SITE_ORIGIN,
          },
          areaServed: DISTRICTS.map((d) => ({ "@type": "AdministrativeArea", name: d })),
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_ORIGIN}/services` },
            { "@type": "ListItem", position: 3, name: service.title, item: `${SITE_ORIGIN}/services/${slug}` },
          ],
        },
      ]
    : [];

  useSEO(title, description, { image: valid ? SERVICE_OG_IMAGE[slug] : undefined, jsonLd, noindex: !valid });

  if (!valid) {
    return (
      <SiteLayout>
        <NotFoundContent />
      </SiteLayout>
    );
  }

  // AMC is a dedicated top-level page (/amc), not a /services/:slug detail page — link there
  // directly instead of building a broken /services/amc URL (SERVICE_CONTENT has no "amc" key).
  const related = (content.related || [])
    .map((rSlug) => {
      const s = SERVICES.find((sv) => sv.slug === rSlug);
      if (!s) return null;
      return { ...s, href: rSlug === "amc" ? "/amc" : `/services/${rSlug}` };
    })
    .filter(Boolean);

  return (
    <SiteLayout>
      <PageHero eyebrow="Our Services" title={service.title} subtitle={service.desc} />

      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <Breadcrumbs items={[{ label: "Services", to: "/services" }, { label: service.title }]} />

        <div className="mt-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <div data-testid="service-overview">
              <SectionTag>Overview</SectionTag>
              <p className="text-secondary/90 leading-relaxed text-base sm:text-lg">{content.overview}</p>
            </div>

            <div data-testid="service-what-we-provide">
              <SectionTag>What We Provide</SectionTag>
              <ul className="grid sm:grid-cols-2 gap-3">
                {content.whatWeProvide.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-secondary/90">
                    <CheckCircle2 size={16} className="text-primary mt-0.5 shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="service-suitable-for">
              <SectionTag>Suitable For</SectionTag>
              <div className="flex flex-wrap gap-2.5">
                {content.suitableFor.map((b) => (
                  <span key={b} className="text-sm px-3.5 py-1.5 rounded-full border border-border text-secondary/90 bg-muted/50 flex items-center gap-1.5">
                    <Building2 size={13} className="text-primary" /> {b}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-sm border border-border p-7 bg-card">
              <Wrench className="text-primary mb-3" size={26} />
              <h3 className="font-heading font-bold text-lg text-secondary">Need {service.short}?</h3>
              <p className="mt-2 text-sm text-muted-foreground">Request a free site visit or a quotation for {service.title.toLowerCase()} at your property.</p>
              <div className="mt-5 flex flex-col gap-3">
                <Button onClick={() => openQuote()} className="w-full rounded-sm bg-primary hover:bg-primary/90 font-semibold" data-testid="service-detail-quote-btn">GET A QUOTE</Button>
                <Link to="/contact">
                  <Button variant="outline" className="w-full rounded-sm border-secondary/20 text-secondary hover:bg-secondary hover:text-white" data-testid="service-detail-contact-btn">CONTACT US</Button>
                </Link>
              </div>
            </div>

            {related.length > 0 && (
              <div className="rounded-sm border border-border p-7 bg-card" data-testid="service-related">
                <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-secondary mb-4">Related Services</h3>
                <div className="flex flex-col gap-3">
                  {related.map((r) => (
                    <Link key={r.slug} to={r.href} className="flex items-center gap-2.5 text-sm text-secondary/90 hover:text-primary transition-colors" data-testid={`service-related-${r.slug}`}>
                      <r.icon size={16} className="text-primary shrink-0" /> {r.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="bg-secondary text-white">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Explore Our Full Range of Services</h2>
          <p className="mt-3 text-slate-300 max-w-xl mx-auto">SecureFlame provides complete fire protection engineering — from detection to suppression — across Tripura.</p>
          <Link to="/services">
            <Button size="lg" className="mt-6 rounded-sm bg-primary hover:bg-primary/90 font-semibold gap-2" data-testid="service-detail-all-services-btn">VIEW ALL SERVICES <ArrowRight size={16} /></Button>
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
